<template>
  <section v-if="product === PRODUCT_TYPE.SURVEY" class="form_settings">
    <Switch
      :value="store.formAction.fieldSettingEnable"
      :label="t('survey.oneQuestionAtTime')"
      @update:value="handleOneQuestionToggle"
    />
    <Switch
      :value="store.builderConfig.stickyContact"
      :label="t('common.stickyContact')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.stickyContact"
      @update:value="updateStickyContact"
    />
    <Switch
      v-if="product === PRODUCT_TYPE.SURVEY && !store?.builderConfig?.newFooter"
      :value="store.builderConfig.isBackButtonEnable"
      :label="t('survey.backButton')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.backButton"
      @update:value="updateBackButtonEnable"
    />
    <Switch
      v-if="product === PRODUCT_TYPE.SURVEY"
      :value="isAutoNavigationEnabled"
      :label="t('survey.autoNavigation')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.autoNavigation"
      @update:value="updateIsAutoNavigationEnabled"
    />
    <Switch
      v-if="product === PRODUCT_TYPE.SURVEY && !store?.builderConfig?.newFooter"
      :value="store.builderConfig.isProgressBarEnabled"
      :label="t('survey.progressBar')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.progressBar"
      @update:value="updateIsProgressBarEnabled"
    />
    <Switch
      v-if="product === PRODUCT_TYPE.SURVEY && !store?.builderConfig?.newFooter"
      :value="store.builderConfig.isAnimationDisabled"
      :label="t('survey.disableAnimation')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.disableAnimation"
      @update:value="updateIsAnimationDisabled"
    />
    <Switch
      v-if="product === PRODUCT_TYPE.SURVEY"
      :value="store.builderConfig.isSurveyScrollEnabled"
      :label="t('survey.scrollToTop')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.scrollToTop"
      @update:value="updateIsSurveyScrollEnabled"
    />
    <Switch
      :label="t('common.useGDPRCompliantFont')"
      :value="store.builderConfig.isGDPRCompliant"
      @update:value="handleGDPRCompliant"
    />
    <Switch
      v-model:value="store.builderConfig.enableTimezone"
      :label="t('common.enableTimezone')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.enableTimezoneSurvey"
      @update:value="store.anyUnsavedChanges = true"
    />
  </section>
  <section v-else-if="product === PRODUCT_TYPE.QUIZ" class="form_settings">
    <Switch
      :value="store.builderConfig.stickyContact"
      :label="t('common.stickyContact')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.stickyContact"
      @update:value="updateStickyContact"
    />
    <Switch
      :value="store.builderConfig.isBackButtonEnable"
      :label="t('survey.backButton')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.backButton"
      @update:value="updateBackButtonEnable"
    />
    <Switch
      :value="store.builderConfig.isAnimationDisabled"
      :label="t('survey.disableAnimation')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.disableAnimation"
      @update:value="updateIsAnimationDisabled"
    />
    <Switch
      :value="store.builderConfig.isSurveyScrollEnabled"
      :label="t('survey.scrollToTop')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.scrollToTop"
      @update:value="updateIsSurveyScrollEnabled"
    />
    <Switch
      :label="t('common.useGDPRCompliantFont')"
      :value="store.builderConfig.isGDPRCompliant"
      @update:value="handleGDPRCompliant"
    />
    <Switch
      v-model:value="store.builderConfig.enableTimezone"
      :label="t('common.enableTimezone')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.enableTimezoneSurvey"
      @update:value="store.anyUnsavedChanges = true"
    />
  </section>
  <section v-else class="form_settings">
    <Switch
      :value="store.builderConfig.stickyContact"
      :label="t('common.stickyContact')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.stickyContact"
      @update:value="updateStickyContact"
    />
    <Switch
      :label="t('common.useGDPRCompliantFont')"
      :value="store.builderConfig.isGDPRCompliant"
      @update:value="handleGDPRCompliant"
    />
    <Switch
      v-model:value="store.builderConfig.enableTimezone"
      :label="t('common.enableTimezone')"
      :tooltip="true"
      :tooltip-message="tooltipMessage.enableTimezoneForm"
      @update:value="store.anyUnsavedChanges = true"
    />
  </section>
  <AlertModal
    :show-modal-popup="openAlertModal"
    :title="titleMessage"
    :description="descriptionMessage"
    :positive-text="t('common.proceed')"
    :negative-text="t('common.cancel')"
    header-type="warning"
    footer-type="warning"
    @pop:change="handleModalCancel"
    @pop:positive="handleModalConfirm"
    @pop:negative="handleModalCancel"
  />
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE, tooltipMessage } from "@/util/constants";
import {
  convertToOffState,
  mergeAllFieldsInSlide,
  splitOneFieldPerSlide,
} from "@/util/oneQuestionMethods";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import AlertModal from "../AlertModal.vue";
import Switch from "../Elements/Switch.vue";

const { t } = useI18n();
const store = useAppStore();
const openAlertModal = ref(false);
const pendingToggleValue = ref(false);

defineProps({
  product: {
    type: String,
    default: PRODUCT_TYPE.FORM,
    required: true,
  },
});

const isAutoNavigationEnabled = computed(() => {
  return !store.builderConfig.disableAutoNavigation;
});

const titleMessage = computed(() => {
  return !store.formAction.fieldSettingEnable
    ? t("survey.switchToOneQuestionAtTimeOn")
    : t("survey.switchToOneQuestionAtTimeOff");
});
const descriptionMessage = computed(() => {
  return !store.formAction.fieldSettingEnable
    ? t("survey.switchToOneQuestionAtTimeOnWarning")
    : t("survey.switchToOneQuestionAtTimeOffWarning");
});
const updateIsAutoNavigationEnabled = (value: boolean) => {
  updatedisableAutoNavigation(!value);
};

const handleOneQuestionToggle = (value: boolean) => {
  pendingToggleValue.value = value;
  openAlertModal.value = true;
};

const handleModalConfirm = () => {
  updateOneQuestionAtTime(pendingToggleValue.value);
  openAlertModal.value = false;
};

const handleModalCancel = () => {
  openAlertModal.value = false;
};

const updateOneQuestionAtTime = async (value: boolean) => {
  store.formAction.fieldSettingEnable = value;
  store.anyUnsavedChanges = true;
  await slideManageV2(value);
  removeJumpToConditions();
};
const updateStickyContact = (value: boolean) => {
  store.builderConfig.stickyContact = value;
  store.anyUnsavedChanges = true;
};
const updateBackButtonEnable = (value: boolean) => {
  store.builderConfig.isBackButtonEnable = value;
  store.anyUnsavedChanges = true;
};
const updatedisableAutoNavigation = (value: boolean) => {
  store.builderConfig.disableAutoNavigation = value;
  store.anyUnsavedChanges = true;
};
const updateIsProgressBarEnabled = (value: boolean) => {
  store.builderConfig.isProgressBarEnabled = value;
  store.anyUnsavedChanges = true;
};
const updateIsAnimationDisabled = (value: boolean) => {
  store.builderConfig.isAnimationDisabled = value;
  store.anyUnsavedChanges = true;
};
const updateIsSurveyScrollEnabled = (value: boolean) => {
  store.builderConfig.isSurveyScrollEnabled = value;
  store.anyUnsavedChanges = true;
};

const handleGDPRCompliant = (val: boolean) => {
  store.builderConfig.isGDPRCompliant = val;
  store.anyUnsavedChanges = true;
};

const slideManageV2 = async (isOneQuestionEnabled) => {
  const tempKey = "survey_slides_" + store.surveyId;
  if (isOneQuestionEnabled) {
    saveToLocalStorage(tempKey, store.slides);
    const mergeSlide = mergeAllFieldsInSlide(store.slides);
    store.slides = splitOneFieldPerSlide(mergeSlide, true);
  } else {
    const ogSlides = getFromLocalStorage(tempKey);
    const oneQuestionSlides = store.slides;
    const originalSlides = convertToOffState(ogSlides, oneQuestionSlides);
    if (originalSlides.length) {
      store.slides = originalSlides;
    }
  }
};

const removeJumpToConditions = () => {
  if (store.builderConfig.conditionalLogic && store.builderConfig.conditionalLogic.length > 0) {
    store.builderConfig.conditionalLogic = store.builderConfig.conditionalLogic.filter(
      (logic) => logic.outcome?.type !== "jumpTo"
    );
    store.anyUnsavedChanges = true;
  }
};

const saveToLocalStorage = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};
const getFromLocalStorage = (key) => {
  return JSON.parse(localStorage.getItem(key));
};
</script>

<style>
.form_settings {
  display: flex;
  align-self: stretch;
  justify-items: flex-start;
  flex-direction: column;
  gap: 16px;
}
</style>
